import React, { Suspense, useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import SEOMetadata from '@/components/SEOMetadata';
import Breadcrumb from '@/components/landing/common/Breadcrumb';
import { breakpoints } from '@/layouts/breakpoints';
import { useIdlePrefetch } from '@/hooks/useIdlePrefetch';

const loadGetStarted = () => import('@/components/landing/GetStarted');
const GetStarted = React.lazy(loadGetStarted);
const loadFooter = () => import('@/components/landing/Footer');
const Footer = React.lazy(loadFooter);

// All .tsx files in this directory — excludes this wrapper and the index page.
const comparisonModules = import.meta.glob('/src/pages/compare/*.tsx', { eager: false });
const EXCLUDED = new Set([
  '/src/pages/compare/ComparisonPage.tsx',
  '/src/pages/compare/CompareIndexPage.tsx',
]);

function resolveCompareModule(
  slug: string
): (() => Promise<{ meta: { title: string; description: string; canonicalUrl: string }; default: React.ComponentType }>) | undefined {
  const key = `/src/pages/compare/${slug}.tsx`;
  if (EXCLUDED.has(key)) return undefined;
  const importer = comparisonModules[key];
  if (!importer) return undefined;
  return importer as any;
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  min-height: 100vh;
  background: #fff;
  padding-top: 120px;
  @media (max-width: ${breakpoints.medium}) {
    padding-top: 80px;
  }

`;

const Container = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 48px;

  @media (max-width: ${breakpoints.medium}) {
    padding: 48px 32px;
  }
`;

const BreadcrumbWrap = styled.div`
  margin-bottom: 32px;
`;

const Title = styled.h1`
  font-size: 38px;
  color: #111;
  margin-bottom: 16px;
  letter-spacing: -0.02em;

  @media (max-width: ${breakpoints.medium}) {
    font-size: 48px!important;
  }
`;

const Description = styled.p`
  font-size: 17px;
  line-height: 1.65;
  color: #555;
  margin-bottom: 40px;
`;

const Divider = styled.div`
  border-top: 1px solid rgba(0, 0, 0, 0.08);
  margin: 40px 0;
`;

const Content = styled.div`
  font-size: 16px;
  line-height: 1.7;
  color: #333;
`;

const NotFound = styled.div`
  min-height: 60vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;

  p {
    font-size: 18px;
    color: #888;
  }

  a {
    font-size: 14px;
    color: #0a0a0a;
    text-decoration: underline;
  }
`;

const LoadingState = styled.div`
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #aaa;
  font-size: 15px;
`;

// ─── Component ───────────────────────────────────────────────────────────────

export default function ComparisonPage() {
  useIdlePrefetch([loadGetStarted, loadFooter]);
  const { slug } = useParams();
  const [mod, setMod] = useState<{
    meta: { title: string; description: string; canonicalUrl: string };
    default: React.ComponentType;
  } | null>(null);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) { setNotFound(true); return; }

    const importer = resolveCompareModule(slug);
    if (!importer) { setNotFound(true); return; }

    let cancelled = false;
    importer().then(m => {
      if (!cancelled) {
        setMod({ meta: m.meta, default: m.default });
      }
    });
    return () => { cancelled = true; };
  }, [slug]);

  if (notFound) {
    return (
      <NotFound>
        <p>Comparison page not found</p>
        <Link to="/sitemap">Back to sitemap</Link>
      </NotFound>
    );
  }

  if (!mod) {
    return <LoadingState>Loading…</LoadingState>;
  }

  const { meta, default: ContentComponent } = mod;

  return (
    <>
      <SEOMetadata
        title={meta.title}
        description={meta.description}
        canonicalUrl={meta.canonicalUrl}
        ogType="article"
      />

      <Helmet>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "name": meta.title,
          "description": meta.description,
          "url": meta.canonicalUrl,
          "dateModified": new Date().toISOString().slice(0, 10),
          "publisher": {
            "@type": "Organization",
            "name": "AxionQuant",
            "url": "https://axionquant.com"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://axionquant.com"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Comparisons",
              "item": "https://axionquant.com/compare"
            },
            {
              "@type": "ListItem",
              "position": 3,
              "name": meta.title,
              "item": meta.canonicalUrl
            }
          ]
        })}</script>
      </Helmet>

      <Wrapper>
        <Container>
          <BreadcrumbWrap>
            <Breadcrumb
              crumbs={[
                { name: 'Home', path: '/' },
                { name: 'Comparisons', path: '/compare' },
                { name: meta.title, path: `/compare/${slug}` },
              ]}
            />
          </BreadcrumbWrap>

          <Title className="serif">{meta.title}</Title>
          <Description>{meta.description}</Description>
          <Divider />

          <Content>
            <ContentComponent />
          </Content>
        </Container>
      </Wrapper>

      <Suspense fallback={null}>
        <GetStarted />
        <Footer />
      </Suspense>
    </>
  );
}
